// src/dashboard/DashboardLayout.jsx
import React, { useContext, useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Tooltip,
  Badge,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AiOutlineMenu,
  AiOutlineDashboard,
  AiOutlineFileText,
  AiOutlineSolution,
  AiOutlineUser,
  AiOutlineUserAdd,
  AiOutlineStar,
  AiOutlineCheckCircle,
  AiOutlineSetting,
  AiOutlineLogout,
  AiOutlineBell,
  AiOutlineMenuFold,
  AiOutlineMenuUnfold,
} from 'react-icons/ai';
import { FaUserCheck, FaLeaf } from 'react-icons/fa';
import { MdOutlineReportProblem, MdOutlineReviews } from 'react-icons/md';
import { AuthContext } from '../auth/AuthContext';
import { StatsContext } from '../stats/StatsContext';
import logo from '../images/logo.png';

const drawerWidth = 280;
const collapsedDrawerWidth = 72;

const DashboardLayout = () => {
  const { currentUser, userLogout } = useContext(AuthContext);
  const { stats } = useContext(StatsContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleLogout = () => {
    userLogout();
    // navigate('/login');
  };
  useEffect(() => {
    if(!currentUser) return navigate('/login')
  }, [])
  
// console.log(currentUser)
  // Dashboard navigation items with badges from stats
  const navItems = [
    {
      section: 'Main',
      items: [
        { 
          text: 'Dashboard', 
          icon: <AiOutlineDashboard />, 
          path: '/dashboard',
          role: ['all'],
          badge: null,
        },
        { 
          text: 'Issues', 
          icon: <MdOutlineReportProblem />, 
          path: '/dashboard/issues',
          role: ['all'],
          badge: stats?.all_issues?.total,
        },
        { 
          text: 'Pending Reports', 
          icon: <AiOutlineFileText />, 
          path: '/dashboard/reports/pending',
          role:['admin', 'specialist'],
          badge: stats?.all_reports?.pending,
        },
      ]
    },
    {
      section: 'Management',
      items: [
        { 
          text: 'My Reports', 
          icon: <AiOutlineFileText />, 
          path: '/dashboard/reports/all',
          role: ['all'],
          // badge: stats?.my_reports?.total,
        },
        { 
          text: 'My Tasks', 
          icon: <AiOutlineCheckCircle />, 
          path: '/dashboard/issues/assigned',
          role: ['admin', 'specialist'],
          badge: (stats?.tasked_issues?.pending + stats?.tasked_issues?.in_progress) || 0,
        },
        { 
          text: 'Tagged Reports', 
          icon: <FaUserCheck />, 
          path: '/dashboard/my-issues',
          role: ['admin'],
          badge: (stats?.tagged_issues?.pending + stats?.tagged_issues?.in_progress) || 0,
        },
        { 
          text: 'Proposals', 
          icon: <AiOutlineSolution />, 
          path: '/dashboard/issues/solutions',
          role: ['admin'],
          badge: stats?.tagged_issues?.proposals,
        },
        { 
          text: 'Reviews', 
          icon: <MdOutlineReviews />, 
          path: '/dashboard/reviews',
          role: ['all'],
          badge: null,
        },
        { 
          text: 'My Reviews', 
          icon: <AiOutlineStar />, 
          path: '/dashboard/my-reviews',
          role: ['all'],
          // badge: stats?.my_reviews?.total,
        },
      ]
    },
    // {
    //   section: 'Community',
    //   items: [
    //     { 
    //       text: 'Events', 
    //       icon: <AiOutlineBell />, 
    //       path: '/dashboard/events',
    //       role: ['all'],
    //       badge: null,
    //     },
    //     { 
    //       text: 'Participations', 
    //       icon: <AiOutlineCheckCircle />, 
    //       path: '/dashboard/events/my-participations',
    //       role: ['all'],
    //       badge: null,
    //     },
    //   ]
    // },
    {
      section: 'Administration',
      items: [
        { 
          text: 'Authorities', 
          icon: <AiOutlineUserAdd />, 
          path: '/dashboard/admin-users',
          role: ['admin', 'support'],
          badge: null,
        },
        { 
          text: 'User Management', 
          icon: <AiOutlineUserAdd />, 
          path: '/dashboard/users/management',
          role: ['support'],
          badge: null,
        },
        { 
          text: 'Contact Submissions', 
          icon: <AiOutlineFileText />, 
          path: '/dashboard/contact',
          role: ['support'],
          badge: stats?.contacts?.new,
        },
      ]
    },
    {
      section: 'Profile',
      items: [
        { 
          text: 'My Profile', 
          icon: <AiOutlineUser />, 
          path: '/dashboard/profile',
          role: ['all'],
          badge: null,
        },
        { 
          text: 'Change Password', 
          icon: <AiOutlineSetting />, 
          path: '/dashboard/password/change',
          role: ['all'],
          badge: null,
        },
        { 
          text: 'Contact Support', 
          icon: <AiOutlineUser />, 
          path: '/dashboard/contact/form',
          role: ['all'],
          badge: null,
        },
      ]
    }
  ];

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      bgcolor: 'rgb(0,40,15)',
      overflow: 'hidden',
    }}>
      {/* Brand Header */}
      <Box
        sx={{
          p: collapsed ? 1.5 : 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: collapsed ? 0 : 2,
          borderBottom: '1px solid rgba(165,214,167,0.15)',
          bgcolor: 'rgb(0,50,20)',
          minHeight: 72,
        }}
      >
        <Avatar 
          src={logo} 
          alt="MazGuard Logo"
          sx={{ 
            width: collapsed ? 36 : 48, 
            height: collapsed ? 36 : 48,
            bgcolor: 'transparent',
            '& img': {
              objectFit: 'contain',
            }
          }}
        >
          <FaLeaf size={collapsed ? 18 : 24} color="#a5d6a7" />
        </Avatar>
        {!collapsed && (
          <Box>
            <Typography variant="h6" sx={{ color: '#a5d6a7', fontWeight: 'bold' }}>
              MazGuard
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(165,214,167,0.6)' }}>
              Environmental Platform
            </Typography>
          </Box>
        )}
      </Box>

      {/* User Info */}
      {!collapsed && (
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: '1px solid rgba(165,214,167,0.15)',
            bgcolor: 'rgb(0,45,18)',
          }}
        >
          <Avatar sx={{ bgcolor: '#2e7d32', width: 40, height: 40, color: '#a5d6a7' }}>
            {currentUser?.profile ? (<img src={currentUser?.profile} alt="Logo" className="img-fluid"/>) :(<> {currentUser?.name?.charAt(0) || 'U'}</>)}
           
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: '#a5d6a7', fontWeight: 600 }}>
              {currentUser?.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(165,214,167,0.6)', textTransform: 'capitalize' }}>
              {currentUser?.role || 'User'}
            </Typography>
          </Box>
          <Chip
            label="Online"
            size="small"
            sx={{
              bgcolor: '#4caf50',
              color: 'rgb(0,40,15)',
              fontWeight: 600,
              fontSize: '0.65rem',
              height: 20,
            }}
          />
        </Box>
      )}

      {/* Collapsed User Avatar */}
      {collapsed && (
        <Box
          sx={{
            p: 1.5,
            display: 'flex',
            justifyContent: 'center',
            borderBottom: '1px solid rgba(165,214,167,0.15)',
            bgcolor: 'rgb(0,45,18)',
          }}
        >
          <Tooltip title={currentUser?.name || 'User'} placement="right">
            <Avatar sx={{ bgcolor: '#2e7d32', width: 36, height: 36, color: '#a5d6a7' }}>
              {currentUser?.name?.charAt(0) || 'U'}
            </Avatar>
          </Tooltip>
        </Box>
      )}

      {/* Navigation */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', 
        py: 2,
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(165,214,167,0.1)',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(165,214,167,0.3)',
          borderRadius: '4px',
        },
      }}>
        {navItems.map((section, idx) => {
          // Filter items based on user role
          const visibleItems = section.items.filter(item =>
            item.role.includes('all') || item.role.includes(currentUser?.role)
          );
          if (visibleItems.length === 0) return null;

          return (
            <Box key={idx} sx={{ mb: 2 }}>
              {!collapsed && (
                <Typography
                  variant="caption"
                  sx={{
                    px: 3,
                    py: 1,
                    color: 'rgba(165,214,167,0.4)',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    letterSpacing: '1px',
                    display: 'block',
                  }}
                >
                  {section.section}
                </Typography>
              )}
              <List sx={{ px: collapsed ? 0.5 : 1 }}>
                {visibleItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <ListItem key={item.text} disablePadding>
                      <Tooltip 
                        title={collapsed ? item.text : ''} 
                        placement="right"
                        arrow
                      >
                        <ListItemButton
                          onClick={() => {
                            navigate(item.path);
                            if (isMobile) setMobileOpen(false);
                          }}
                          sx={{
                            borderRadius: 2,
                            mb: 0.5,
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            px: collapsed ? 1 : 2,
                            py: collapsed ? 1.5 : 1,
                            bgcolor: isActive ? 'rgba(46,125,50,0.2)' : 'transparent',
                            '&:hover': {
                              bgcolor: 'rgba(46,125,50,0.15)',
                            },
                            borderLeft: isActive ? '3px solid #a5d6a7' : '3px solid transparent',
                            minHeight: 40,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              color: isActive ? '#a5d6a7' : 'rgba(165,214,167,0.6)',
                              minWidth: collapsed ? 0 : 40,
                              justifyContent: 'center',
                            }}
                          >
                            {item.badge ? (
                              <Badge 
                                badgeContent={item.badge} 
                                color="success" 
                                max={99}
                                sx={{
                                  '& .MuiBadge-badge': {
                                    bgcolor: '#4caf50',
                                    color: 'rgb(0,40,15)',
                                    fontWeight: 'bold',
                                    fontSize: '0.6rem',
                                    height: 18,
                                    minWidth: 18,
                                    padding: '0 4px',
                                  }
                                }}
                              >
                                {item.icon}
                              </Badge>
                            ) : (
                              item.icon
                            )}
                          </ListItemIcon>
                          {!collapsed && (
                            <ListItemText
                              primary={
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isActive ? '#a5d6a7' : 'rgba(165,214,167,0.7)',
                                    fontWeight: isActive ? 600 : 400,
                                  }}
                                >
                                  {item.text}
                                </Typography>
                              }
                            />
                          )}
                        </ListItemButton>
                      </Tooltip>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>

      {/* Bottom Actions */}
      <Box sx={{ 
        p: collapsed ? 1 : 2, 
        borderTop: '1px solid rgba(165,214,167,0.15)', 
        bgcolor: 'rgb(0,45,18)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: collapsed ? 'center' : 'stretch',
        gap: 1,
      }}>
        <Tooltip title={collapsed ? 'Logout' : ''} placement="right">
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              justifyContent: collapsed ? 'center' : 'flex-start',
              px: collapsed ? 1 : 2,
              '&:hover': {
                bgcolor: 'rgba(244, 67, 54, 0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ 
              color: 'rgba(165,214,167,0.6)', 
              minWidth: collapsed ? 0 : 40,
              justifyContent: 'center',
            }}>
              <AiOutlineLogout />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ color: 'rgba(165,214,167,0.7)' }}>
                    Logout
                  </Typography>
                }
              />
            )}
          </ListItemButton>
        </Tooltip>
        
        {!collapsed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Typography variant="caption" sx={{ color: 'rgba(165,214,167,0.3)' }}>
              © 2026 MazGuard
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(165,214,167,0.2)' }}>
              v2.0
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar for Mobile */}
      <AppBar
        position="fixed"
        sx={{
          display: { xs: 'flex', md: 'none' },
          background: 'linear-gradient(135deg, rgb(0,40,15), rgb(0,60,25))',
          boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, color: '#a5d6a7' }}
          >
            <AiOutlineMenu />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar src={logo} sx={{ width: 30, height: 30, bgcolor: 'transparent' }}>
              <FaLeaf size={18} color="#a5d6a7" />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#a5d6a7' }}>
              MazGuard
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Chip
            label={currentUser?.role || 'User'}
            size="small"
            sx={{
              bgcolor: '#2e7d32',
              color: '#a5d6a7',
              textTransform: 'capitalize',
            }}
          />
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        sx={{
          width: collapsed ? collapsedDrawerWidth : drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: collapsed ? collapsedDrawerWidth : drawerWidth,
            boxSizing: 'border-box',
            bgcolor: 'rgb(0,40,15)',
            borderRight: '1px solid rgba(165,214,167,0.05)',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box
  component="main"
  sx={{
    position: 'relative',
    overflow: 'hidden',

    backgroundImage:
      "url('https://media.licdn.com/dms/image/v2/D5612AQGhl0gO75T52Q/article-cover_image-shrink_600_2000/article-cover_image-shrink_600_2000/0/1679390033979?e=2147483647&v=beta&t=SRFf75fE0VLfceMpuQTf-VCh5w-mjvR1dik_AstqcmM')",
    backgroundSize: 'cover',
    backgroundAttachment: 'fixed',
    backgroundPosition: 'center',

    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    p: isMobile ? 2 : 4,
    mt: isMobile ? 8 : 0,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),

    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundColor: 'rgba(0, 50, 20, 0.5)', // Adjust darkness here
      zIndex: 0,
    },

    '& > *': {
      position: 'relative',
      zIndex: 1,
    },
  }}
>
        {/* Collapse Toggle Button - Desktop Only */}
        {!isMobile && (
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title={collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'} placement="bottom">
              <IconButton
                onClick={handleToggleCollapse}
                sx={{
                  bgcolor: 'rgba(46,125,50,0.1)',
                  color: '#2e7d32',
                  '&:hover': {
                    bgcolor: 'rgba(46,125,50,0.2)',
                  },
                  border: '1px solid rgba(46,125,50,0.2)',
                }}
              >
                {collapsed ? <AiOutlineMenuUnfold size={24} /> : <AiOutlineMenuFold size={24} />}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Render child routes */}
        <Outlet />
      </Box>
    </Box>
  );
};

export default DashboardLayout;